import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useState, useEffect } from 'react';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import { addAgentURL, getDepartmentsURL, getRolesURL } from '../../../../utils/urls';

const AddAgent = ({ visible, onHide, onAgentAdded }) => {
    const [agent, setAgent] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        idNumber: '',
        department: '',
        team_id: '',
        role: '',
        status: 'active'
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loadingDepartments, setLoadingDepartments] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(false);

    const statuses = [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Training', value: 'training' }
    ];

    useEffect(() => {
        if (visible) {
            fetchDepartments();
            fetchRoles();
        }
    }, [visible]);

    const fetchDepartments = async () => {
        setLoadingDepartments(true);
        try {
            const response = await makeRequest2(getDepartmentsURL, 'GET', {});
            if (response.success && response.data && response.data.success) {
                const deptData = response.data.data || [];
                const deptOptions = deptData
                    .filter(dept => dept.active !== false)
                    .map(dept => ({
                        label: `${dept.name} (${dept.code})`,
                        value: dept.code
                    }));
                setDepartments(deptOptions);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            toastify('Failed to load departments', 'error');
        }
        setLoadingDepartments(false);
    };

    const fetchRoles = async () => {
        setLoadingRoles(true);
        try {
            const response = await makeRequest2(getRolesURL, 'GET', {});
            if (response.success && response.data && response.data.success) {
                const roleData = response.data.data || [];
                const roleOptions = roleData
                    .filter(role => role.active !== false)
                    .map(role => ({
                        label: `${role.name} (Level ${role.level})`,
                        value: role.code
                    }));
                setRoles(roleOptions);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            toastify('Failed to load roles', 'error');
        }
        setLoadingRoles(false);
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const validateName = (name) => {
        // Only allow letters and spaces, no numbers or special characters
        const nameRegex = /^[a-zA-Z\s]+$/;
        return nameRegex.test(name);
    };

    const validatePhoneNumber = (phone) => {
        // Allow numbers, spaces, hyphens, parentheses, and plus sign
        const phoneRegex = /^[\d\s\-\+\(\)]+$/;
        return phoneRegex.test(phone);
    };

    const handleInputChange = (e, field) => {
        let val = (e.target && e.target.value) || '';
        const errors = { ...validationErrors };

        // Handle name fields with capitalization and validation
        if (field === 'firstName' || field === 'lastName') {
            if (val && !validateName(val)) {
                errors[field] = 'Name should only contain letters and spaces';
            } else {
                delete errors[field];
                // Capitalize first letter of each word
                val = val.split(' ').map(word => capitalizeFirstLetter(word)).join(' ');
            }
        }

        // Validate phone number
        if (field === 'phoneNumber' && val && !validatePhoneNumber(val)) {
            errors.phoneNumber = 'Invalid phone number format';
        } else if (field === 'phoneNumber') {
            delete errors.phoneNumber;
        }

        setValidationErrors(errors);
        setAgent(prev => ({
            ...prev,
            [field]: val
        }));
    };

    const saveAgent = async () => {
        setSubmitted(true);

        // Validate required fields
        if (!agent.firstName?.trim() || !agent.lastName?.trim() || !agent.email?.trim() || !agent.phoneNumber?.trim() || !agent.idNumber?.trim()) {
            toastify('Please fill in all required fields', 'error');
            return;
        }

        // Validate department and role
        if (!agent.department?.trim()) {
            toastify('Department is required', 'error');
            return;
        }

        if (!agent.role) {
            toastify('Role is required', 'error');
            return;
        }

        // Check for validation errors
        if (Object.keys(validationErrors).length > 0) {
            toastify('Please fix validation errors before submitting', 'error');
            return;
        }

        setLoading(true);

        try {
            const response = await makeRequest2(addAgentURL, 'POST', agent);

            if (response.success !== false) {
                toastify('Agent added successfully', 'success');
                onAgentAdded();
                resetForm();
                onHide();
            } else {
                toastify(response.message || 'Failed to add agent', 'error');
            }
        } catch (error) {
            toastify(error.message || 'Failed to add agent', 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setAgent({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            idNumber: '',
            department: '',
            team_id: '',
            role: '',
            status: 'active'
        });
        setSubmitted(false);
        setValidationErrors({});
    };

    const handleHide = () => {
        resetForm();
        onHide();
    };

    const dialogFooter = (
        <>
            <Button
                label="Cancel"
                icon="pi pi-times"
                outlined
                onClick={handleHide}
                disabled={loading}
            />
            <Button
                label="Save"
                icon="pi pi-check"
                onClick={saveAgent}
                loading={loading}
            />
        </>
    );

    return (
        <Dialog
            visible={visible}
            style={{ width: '450px' }}
            header="Add New Agent"
            modal
            className="p-fluid"
            footer={dialogFooter}
            onHide={handleHide}
        >
            <div className="field">
                <label htmlFor="firstName">First Name *</label>
                <InputText
                    id="firstName"
                    value={agent.firstName}
                    onChange={(e) => handleInputChange(e, 'firstName')}
                    required
                    autoFocus
                    className={(submitted && !agent.firstName) || validationErrors.firstName ? 'p-invalid' : ''}
                />
                {submitted && !agent.firstName && <small className="p-error">First Name is required.</small>}
                {validationErrors.firstName && <small className="p-error">{validationErrors.firstName}</small>}
            </div>

            <div className="field">
                <label htmlFor="lastName">Last Name *</label>
                <InputText
                    id="lastName"
                    value={agent.lastName}
                    onChange={(e) => handleInputChange(e, 'lastName')}
                    required
                    className={(submitted && !agent.lastName) || validationErrors.lastName ? 'p-invalid' : ''}
                />
                {submitted && !agent.lastName && <small className="p-error">Last Name is required.</small>}
                {validationErrors.lastName && <small className="p-error">{validationErrors.lastName}</small>}
            </div>

            <div className="field">
                <label htmlFor="email">Email *</label>
                <InputText
                    id="email"
                    value={agent.email}
                    onChange={(e) => handleInputChange(e, 'email')}
                    required
                    type="email"
                    className={submitted && !agent.email ? 'p-invalid' : ''}
                />
                {submitted && !agent.email && <small className="p-error">Email is required.</small>}
            </div>

            <div className="field">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <InputText
                    id="phoneNumber"
                    value={agent.phoneNumber}
                    onChange={(e) => handleInputChange(e, 'phoneNumber')}
                    required
                    className={(submitted && !agent.phoneNumber) || validationErrors.phoneNumber ? 'p-invalid' : ''}
                />
                {submitted && !agent.phoneNumber && <small className="p-error">Phone Number is required.</small>}
                {validationErrors.phoneNumber && <small className="p-error">{validationErrors.phoneNumber}</small>}
            </div>

            <div className="field">
                <label htmlFor="idNumber">ID/Passport Number *</label>
                <InputText
                    id="idNumber"
                    value={agent.idNumber}
                    onChange={(e) => handleInputChange(e, 'idNumber')}
                    required
                    className={submitted && !agent.idNumber ? 'p-invalid' : ''}
                />
                {submitted && !agent.idNumber && <small className="p-error">ID/Passport Number is required.</small>}
            </div>

            <div className="field">
                <label htmlFor="department">Department <span className="text-danger">*</span></label>
                <Dropdown
                    id="department"
                    value={agent.department}
                    onChange={(e) => handleInputChange(e, 'department')}
                    options={departments}
                    placeholder="Select Department"
                    className={submitted && !agent.department ? 'p-invalid' : ''}
                />
                {submitted && !agent.department && <small className="p-error">Department is required.</small>}
            </div>

            <div className="field">
                <label htmlFor="team_id">Team ID</label>
                <InputText
                    id="team_id"
                    value={agent.team_id}
                    onChange={(e) => handleInputChange(e, 'team_id')}
                />
            </div>

            <div className="field">
                <label htmlFor="role">Role <span className="text-danger">*</span></label>
                <Dropdown
                    id="role"
                    value={agent.role}
                    onChange={(e) => handleInputChange(e, 'role')}
                    options={roles}
                    placeholder="Select Role"
                    className={submitted && !agent.role ? 'p-invalid' : ''}
                />
                {submitted && !agent.role && <small className="p-error">Role is required.</small>}
            </div>

            <div className="field">
                <label htmlFor="status">Status</label>
                <Dropdown
                    id="status"
                    value={agent.status}
                    onChange={(e) => handleInputChange(e, 'status')}
                    options={statuses}
                    placeholder="Select Status"
                />
            </div>
        </Dialog>
    );
};

export default AddAgent;